// Public customer-facing Help Centre layout. Wraps /helpcentre/:slug/* with
// the help centre's branding and a customer nav (Log in / My requests). The
// Help Centre is the umbrella that lists multiple portals as cards; each card
// drills into the existing per-portal experience at /help/:portalSubdomain.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Outlet, useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Zap } from 'lucide-react';

const HcCtx = createContext(null);
export const useHelpCentre = () => useContext(HcCtx);

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function HelpCentreLayout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [customerChecked, setCustomerChecked] = useState(false);
  const [loading, setLoading] = useState(true);

  const reloadCustomer = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/customer/me`, { withCredentials: true });
      setCustomer(data);
    } catch { setCustomer(null); }
    finally { setCustomerChecked(true); }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/public/help-centre/${slug}`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setData({ error: err?.response?.data?.detail || 'Help centre not found' });
      } finally { if (!cancelled) setLoading(false); }
      reloadCustomer();
    })();
    return () => { cancelled = true; };
  }, [slug, reloadCustomer]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50 text-zinc-400">Loading help centre…</div>;
  if (data?.error) return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="text-center">
        <p className="text-2xl font-bold text-zinc-900 mb-2">Help centre unavailable</p>
        <p className="text-sm text-zinc-500">{data.error}</p>
      </div>
    </div>
  );

  const hc = data.help_centre;
  const theme = hc.branding?.theme_color || '#2563eb';
  const isHome = location.pathname === `/helpcentre/${slug}`;

  const logout = async () => {
    await axios.post(`${API}/customer/logout`, {}, { withCredentials: true });
    setCustomer(null);
    navigate(`/helpcentre/${slug}`);
  };

  return (
    <HcCtx.Provider value={{ ...data, slug, customer, customerChecked, reloadCustomer }}>
      <div className="min-h-screen flex flex-col bg-zinc-50" style={{ '--theme': theme }} data-testid="help-centre-layout">
        <header style={{ background: theme }} className="text-white">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to={`/helpcentre/${slug}`} className="flex items-center gap-2.5" data-testid="hc-home-link">
              {hc.branding?.logo_url ? (
                <img src={hc.branding.logo_url} alt="logo" className={`w-8 h-8 rounded bg-white p-0.5 ${hc.branding?.logo_fit === 'cover' ? 'object-cover' : 'object-contain'}`} />
              ) : (
                <div className="w-8 h-8 rounded bg-white/20 flex items-center justify-center font-bold text-sm">{hc.name.charAt(0)}</div>
              )}
              <div>
                <p className="font-semibold text-sm leading-tight">{hc.name}</p>
                <p className="text-[11px] text-white/70 leading-tight">{data.organization?.name}</p>
              </div>
            </Link>
            <nav className="flex items-center gap-3 text-sm">
              {customer ? (
                <>
                  <Link to={`/helpcentre/${slug}/requests`} className={`px-3 py-1.5 rounded hover:bg-white/15 transition-colors ${location.pathname.endsWith('/requests') ? 'bg-white/20' : ''}`}
                    data-testid="hc-nav-my-requests">My requests</Link>
                  <button onClick={logout} className="px-3 py-1.5 rounded hover:bg-white/15" data-testid="hc-nav-logout">Log out</button>
                  <span className="text-[12px] text-white/80">{customer.name}</span>
                </>
              ) : (
                <Link to={`/helpcentre/${slug}/login`} className="px-3 py-1.5 rounded hover:bg-white/15" data-testid="hc-nav-login">Log in</Link>
              )}
            </nav>
          </div>
          {isHome && (
            <div className="relative">
              {hc.branding?.hero_image_url && (
                <div className={`absolute inset-0 opacity-25 bg-center ${hc.branding?.hero_fit === 'contain' ? 'bg-contain bg-no-repeat' : 'bg-cover'}`} style={{ backgroundImage: `url('${hc.branding.hero_image_url}')` }} aria-hidden />
              )}
              <div className="relative max-w-5xl mx-auto px-6 pt-2 pb-12 text-center">
                <h1 className="text-3xl md:text-4xl font-bold font-['Outfit']">{hc.branding?.intro_text ? `Welcome to ${hc.name}` : 'How can we help?'}</h1>
                <p className="text-white/90 mt-2 max-w-2xl mx-auto">{hc.branding?.intro_text}</p>
              </div>
            </div>
          )}
        </header>

        <main className="flex-1 w-full max-w-5xl mx-auto px-6 py-8">
          <Outlet />
        </main>

        <footer className="border-t border-zinc-200 bg-white mt-8" data-testid="hc-footer">
          <div className="max-w-5xl mx-auto px-6 py-6 flex flex-col items-center gap-2">
            {hc.branding?.footer_text && <p className="text-xs text-zinc-500 text-center">{hc.branding.footer_text}</p>}
            <a href={hc.branding?.powered_by_url || 'https://inspiroworks.com'} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-600 transition-colors">
              <span>Powered by</span>
              <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
              <span className="font-semibold text-zinc-500">{hc.branding?.powered_by_label || 'Inspiro Service Desk'}</span>
            </a>
          </div>
        </footer>
      </div>
    </HcCtx.Provider>
  );
}
